import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { Check, Copy, Loader2, AlertCircle, RefreshCw, Wifi, Terminal } from 'lucide-react'
import Modal from './Modal'
import { hostsApi } from '../../services/managedDevicesApi'
import { isVpnHandshakeFresh } from '../../utils/vpn'

const POLL_INTERVAL = 5000
const SETUP_TIMEOUT_MS = 60 * 60 * 1000
// Depois do script terminar (Ready), prazo pro servidor registrar o handshake do túnel
// (handshake real ≤ ~2 min + sync do stats pro banco ≤ ~1 min).
const VPN_VERIFY_TIMEOUT_MS = 3 * 60 * 1000

export default function ConnectHostModal({ isOpen, onClose, host }) {
  const { t } = useTranslation()
  const isWindowsHost = host?.hostKind === 'Windows'
  const [setupUrl, setSetupUrl] = useState(null)
  const [copied, setCopied] = useState(false)
  const [currentStep, setCurrentStep] = useState(0)
  const [error, setError] = useState(null)
  const [expired, setExpired] = useState(false)
  const [vpnTimedOut, setVpnTimedOut] = useState(false)
  const [loading, setLoading] = useState(false)
  const pollingRef = useRef(null)
  const startTimeRef = useRef(null)
  const readyAtRef = useRef(null)

  const steps = useMemo(
    () => [
      { key: 'waiting', label: t('hosts.connectModal.stepWaiting') },
      { key: 'vpn', label: t('hosts.connectModal.stepVpn') },
      { key: 'ssh', label: t('hosts.connectModal.stepSsh') },
    ],
    [t],
  )

  // Baixa para arquivo e só executa em caso de sucesso (`&&`), depois roda com `sudo`.
  // Motivo: `wget -qO- | bash` era silencioso — `-q` engolia qualquer falha de download
  // (DNS/TLS/rede, ou wget do BusyBox sem HTTPS) e jogava um corpo vazio no pipe, então o
  // `bash` rodava um script vazio e o operador não via NADA (nem o cabeçalho). Além disso,
  // no Raspberry Pi OS o usuário padrão não é root e o script precisa de privilégio
  // (useradd, /etc/sudoers.d, /etc/wireguard, systemctl). `-nv` mantém as mensagens de erro.
  const wgetCommand = setupUrl
    ? `wget -nv -O /tmp/automais-setup.sh "${setupUrl}" && sudo bash /tmp/automais-setup.sh`
    : ''

  const activate = useCallback(async () => {
    if (!host?.id) return
    if (host.hostKind === 'Windows') {
      setSetupUrl(null)
      setError(null)
      setExpired(false)
      setCurrentStep(0)
      setCopied(false)
      return
    }
    setLoading(true)
    setError(null)
    setExpired(false)
    setVpnTimedOut(false)
    readyAtRef.current = null
    setCurrentStep(0)
    setCopied(false)
    try {
      const result = await hostsApi.activateSetup(host.id)
      // /activate-setup retorna ora string pura (ActionResult<string>), ora objeto {url}.
      const url = typeof result === 'string' ? result : (result?.url ?? result?.setupUrl)
      if (!url) throw new Error('setupUrl ausente na resposta da API')
      setSetupUrl(url)
      startTimeRef.current = Date.now()
    } catch (err) {
      setError(err.response?.data?.message || err.message || t('hosts.connectModal.activateError'))
    } finally {
      setLoading(false)
    }
  }, [host?.id, host?.hostKind, t])

  useEffect(() => {
    if (isOpen && host?.id) {
      activate()
    }
    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [isOpen, host?.id, activate])

  useEffect(() => {
    if (!isOpen || isWindowsHost || !setupUrl || currentStep >= 2 || vpnTimedOut) {
      if (pollingRef.current) clearInterval(pollingRef.current)
      return
    }

    // Duas fases: (1) script terminou no host (ProvisioningStatus=Ready via callback);
    // (2) VPN realmente conectou (handshake recente reportado pelo servidor). O callback
    // sozinho NÃO prova túnel — o setup-script continua mesmo se o wg-quick falhar.
    pollingRef.current = setInterval(async () => {
      if (startTimeRef.current && Date.now() - startTimeRef.current > SETUP_TIMEOUT_MS) {
        setExpired(true)
        clearInterval(pollingRef.current)
        return
      }

      try {
        const updated = await hostsApi.getById(host.id)
        if (updated.provisioningStatus === 'Error') {
          setError(t('hosts.connectModal.provisioningError'))
          clearInterval(pollingRef.current)
          return
        }
        if (updated.provisioningStatus === 'Ready' && !readyAtRef.current) {
          readyAtRef.current = Date.now()
          setCurrentStep(1)
        }
        if (readyAtRef.current) {
          if (isVpnHandshakeFresh(updated.vpnPeerLastHandshake)) {
            setCurrentStep(2)
            clearInterval(pollingRef.current)
          } else if (Date.now() - readyAtRef.current > VPN_VERIFY_TIMEOUT_MS) {
            setVpnTimedOut(true)
            clearInterval(pollingRef.current)
          }
        }
      } catch {
        // ignora erros de polling
      }
    }, POLL_INTERVAL)

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current)
    }
  }, [isOpen, setupUrl, host?.id, currentStep, isWindowsHost, vpnTimedOut, t])

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(wgetCommand)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    } catch {
      // fallback
      const el = document.createElement('textarea')
      el.value = wgetCommand
      document.body.appendChild(el)
      el.select()
      document.execCommand('copy')
      document.body.removeChild(el)
      setCopied(true)
      setTimeout(() => setCopied(false), 3000)
    }
  }

  const handleClose = () => {
    if (pollingRef.current) clearInterval(pollingRef.current)
    setSetupUrl(null)
    setCurrentStep(0)
    setError(null)
    setExpired(false)
    setVpnTimedOut(false)
    readyAtRef.current = null
    onClose()
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('hosts.connectModal.title', { name: host?.name || '' })}>
      <div className="space-y-6 max-w-lg">
        {loading && (
          <div className="flex items-center gap-3 text-gray-600">
            <Loader2 className="w-5 h-5 animate-spin" />
            {t('hosts.connectModal.preparing')}
          </div>
        )}

        {error && (
          <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-800">{error}</p>
              <button
                type="button"
                className="mt-2 text-sm text-red-600 underline"
                onClick={activate}
              >
                {t('common.buttons.retry')}
              </button>
            </div>
          </div>
        )}

        {isWindowsHost && !error && (
          <>
            <div className="border border-blue-200 bg-blue-50 rounded-lg p-4 space-y-2">
              <p className="text-sm text-blue-900 font-medium">
                {t('hosts.connectModal.windowsFlowTitle')}
              </p>
              <p className="text-sm text-blue-800">
                <Trans
                  i18nKey="hosts.connectModal.windowsFlowStep1"
                  components={[<strong key="0" />]}
                />
              </p>
              <p className="text-sm text-blue-800">
                {t('hosts.connectModal.windowsFlowStep2')}
              </p>
              <p className="text-sm text-blue-800">
                {t('hosts.connectModal.windowsFlowStep3')}
              </p>
              <p className="text-sm text-blue-800">
                {t('hosts.connectModal.windowsFlowStep4')}
              </p>
            </div>
          </>
        )}

        {setupUrl && !error && !isWindowsHost && (
          <>
            <div>
              <p className="text-sm text-gray-700 mb-3">
                <Trans
                  i18nKey="hosts.connectModal.runCommand"
                  components={[<strong key="0" />]}
                />
              </p>
              <div className="relative">
                <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto whitespace-pre-wrap break-all">
                  {wgetCommand}
                </pre>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="absolute top-2 right-2 p-2 bg-gray-700 hover:bg-gray-600 rounded-md transition-colors"
                  title={t('hosts.connectModal.copyCommand')}
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-400" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-300" />
                  )}
                </button>
              </div>
              {copied && (
                <p className="text-xs text-green-600 mt-1">{t('hosts.connectModal.commandCopied')}</p>
              )}
            </div>

            <div className="border border-gray-200 rounded-lg p-4">
              <p className="text-sm font-medium text-gray-700 mb-3">{t('hosts.connectModal.provisioningStatus')}</p>
              <div className="space-y-3">
                {steps.map((step, idx) => {
                  const isDone = idx < currentStep
                  const isCurrent = idx === currentStep && currentStep < 2
                  const isPending = idx > currentStep
                  return (
                    <div key={step.key} className="flex items-center gap-3">
                      {isDone && (
                        <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                      )}
                      {isCurrent && !expired && !vpnTimedOut && (
                        <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                          <Loader2 className="w-4 h-4 text-blue-600 animate-spin" />
                        </div>
                      )}
                      {isCurrent && (expired || vpnTimedOut) && (
                        <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                          <AlertCircle className="w-4 h-4 text-amber-600" />
                        </div>
                      )}
                      {isPending && (
                        <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                          <div className="w-2 h-2 rounded-full bg-gray-300" />
                        </div>
                      )}
                      <span
                        className={
                          isDone
                            ? 'text-sm text-green-700 font-medium'
                            : isCurrent
                              ? 'text-sm text-gray-900'
                              : 'text-sm text-gray-400'
                        }
                      >
                        {idx === 1 && <Wifi className="w-4 h-4 inline mr-1" />}
                        {idx === 2 && <Terminal className="w-4 h-4 inline mr-1" />}
                        {step.label}
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>

            {vpnTimedOut && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    {t('hosts.connectModal.vpnTimeoutMessage')}
                  </p>
                  <button
                    type="button"
                    className="mt-2 btn btn-primary btn-sm inline-flex items-center gap-1"
                    onClick={activate}
                  >
                    <RefreshCw className="w-3 h-3" />
                    {t('hosts.connectModal.expiredRegenerate')}
                  </button>
                </div>
              </div>
            )}

            {expired && (
              <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm text-amber-800">
                    {t('hosts.connectModal.expiredMessage')}
                  </p>
                  <button
                    type="button"
                    className="mt-2 btn btn-primary btn-sm inline-flex items-center gap-1"
                    onClick={activate}
                  >
                    <RefreshCw className="w-3 h-3" />
                    {t('hosts.connectModal.expiredRegenerate')}
                  </button>
                </div>
              </div>
            )}

            {currentStep >= 2 && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-center">
                <Check className="w-8 h-8 text-green-600 mx-auto mb-2" />
                <p className="text-green-800 font-medium">{t('hosts.connectModal.successTitle')}</p>
                <p className="text-sm text-green-700 mt-1">
                  {t('hosts.connectModal.successDetail')}
                </p>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end pt-2">
          <button type="button" className="btn btn-ghost" onClick={handleClose}>
            {currentStep >= 2 ? t('hosts.connectModal.closeDone') : t('hosts.connectModal.close')}
          </button>
        </div>
      </div>
    </Modal>
  )
}
